import React, { useEffect, useState } from 'react';
import {
  formatCurrency,
  formatDateTime,
  formatFileSize,
  getStatusMeta,
} from '../../lib/format';

function openDownload(url, name) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.target = '_blank';
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export default function OrderDetailDrawer({
  order,
  isOpen,
  onClose,
  onStatusUpdate,
}) {
  const [nextStatus, setNextStatus] = useState(order?.status || 'paid');

  useEffect(() => {
    setNextStatus(order?.status || 'paid');
  }, [order]);

  if (!order) {
    return null;
  }

  const statusMeta = getStatusMeta(order.status);
  const files = order.files || [];

  return (
    <div
      className={`admin-drawer-backdrop ${isOpen ? 'open' : ''}`}
      role="presentation"
      onClick={onClose}
    >
      <aside
        className="admin-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-drawer-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="drawer-back-btn" onClick={onClose}>
          Back to orders
        </button>

        <div className="drawer-hero">
          <span className={`status-pill tone-${statusMeta.tone}`}>{statusMeta.label}</span>
          <h3 id="admin-drawer-title">{order.orderId || order.id}</h3>
          <p>Placed on {formatDateTime(order.createdAt)}</p>
        </div>

        <section className="drawer-section">
          <h4>Customer</h4>
          <div className="drawer-row">
            <span>Name</span>
            <strong>{order.customer?.name || '--'}</strong>
          </div>
          <div className="drawer-row">
            <span>Phone</span>
            <strong>{order.customer?.phone || '--'}</strong>
          </div>
          <div className="drawer-row">
            <span>Email</span>
            <strong>{order.customer?.email || '--'}</strong>
          </div>
          <div className="drawer-row multiline">
            <span>Address</span>
            <strong>{order.customer?.address || '--'}</strong>
          </div>
        </section>

        <section className="drawer-section">
          <h4>Print Specifications</h4>
          <div className="drawer-row">
            <span>Type</span>
            <strong>
              {order.printOptions?.type === 'color' ? 'Full Color' : 'Black and White'}
            </strong>
          </div>
          <div className="drawer-row">
            <span>Paper Size</span>
            <strong>{order.printOptions?.size || 'A4'}</strong>
          </div>
          <div className="drawer-row">
            <span>Pages</span>
            <strong>{order.printOptions?.pages || 1}</strong>
          </div>
          <div className="drawer-row">
            <span>Copies</span>
            <strong>{order.printOptions?.copies || 1}</strong>
          </div>
          <div className="drawer-row">
            <span>Binding</span>
            <strong>{order.printOptions?.binding || 'none'}</strong>
          </div>
          <div className="drawer-row">
            <span>Delivery</span>
            <strong>{order.printOptions?.delivery || 'standard'}</strong>
          </div>
          {order.notes ? (
            <div className="drawer-row multiline">
              <span>Notes</span>
              <strong>{order.notes}</strong>
            </div>
          ) : null}
          <div className="drawer-total">
            <span>Total Paid</span>
            <strong>{formatCurrency(order.totalAmount || 0)}</strong>
          </div>
        </section>

        <section className="drawer-section">
          <h4>Attached Files</h4>
          {files.length ? (
            <div className="drawer-files">
              {files.map((file) => (
                <div className="drawer-file-card" key={`${file.path || file.url}-${file.name}`}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>{formatFileSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="inline-action"
                    onClick={() => openDownload(file.url, file.name)}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="drawer-empty">No files attached to this order.</p>
          )}
        </section>

        <section className="drawer-section">
          <h4>Update Status</h4>
          <select
            className="admin-select"
            value={nextStatus}
            onChange={(event) => setNextStatus(event.target.value)}
          >
            <option value="paid">Paid - Awaiting Print</option>
            <option value="printed">Printed</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            type="button"
            className="btn-primary full-width"
            onClick={() => onStatusUpdate(order.orderId, nextStatus)}
          >
            Update Status
          </button>
        </section>
      </aside>
    </div>
  );
}
